import $i18n from '@alife/panda-i18n';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Recorder from 'recorder-core';
import useTimer from './use-timer';
import { CnMessage } from '@cainiaofe/cn-ui';

const hideRecorderLog = true;
if (hideRecorderLog) {
  Recorder.CLog = () => {};
}

const WEBSOCKET_URL = 'wss://cainiao-asr-ws.cainiao-inc.com';

function getCommonWSParams() {
  return {
    chunk_size: [5, 10, 5],
    wav_name: 'h5',
    chunk_interval: 10,
    mode: '2pass',
    bits_per_sample: 16,
    wav_format: 'pcm',
    oss_flag: 'true', // 控制是否要把文本存到文件里
    unixtime: `${Math.floor(new Date().getTime() / 1000)}`,
  };
}

const TEXT_BUFFER_MAX_LENGTH = 200;
const MODE_OFFLINE = '2pass-offline';
const MODE_ONLINE = '2pass-online';
const textBuffer = {
  messageId: '',
  offline: '',
  online: '',
  current: '',
};

/** 获取到文本后的填充模式 */
export enum FILL_MODE {
  /** 填充到消息 */
  MESSAGE = 'message',
  /** 填充到输入框 */
  INPUT = 'input',
}

/** 录音状态 */
export enum RECORD_STATUS {
  /** 初始 */
  DEFAULT = 1,
  /** 录音中 */
  RECORDING = 2,
  /** 暂停 */
  PAUSED = 3,
}

/** 录音回调消息 */
export interface IRecordMessage {
  /** id */
  id?: string;
  /** 是否格式化 */
  format?: boolean;
  /** 转译的文本 */
  text: string;
  /** 消息类型，add表示新增消息，update表示更新消息，setInputText表示填充输入框文本 */
  method: 'add' | 'update' | 'setInputText';
}

/** 开始录音的参数 */
interface StartRecordOptions {
  /** 会话id，用于唯一标识会话，必填 */
  conversationId: string;
  /** 填充模式，input表示填充进输入框, message表示填充进消息气泡 */
  fillMode: 'input' | 'message';
  /** 高亮关键字 */
  keywords?: string[];
  /** 收音的最小音量，1-100 */
  minPowerLevel?: number;
  /** 收音的最大音量，1-100 */
  maxPowerLevel?: number;
}

/** 录音信息 */
export interface RecordInfo {
  /** 录音状态 */
  recordStatus: RECORD_STATUS;
  /** 当前录音时长 */
  recordTime: string;
  /** 开始录音 */
  startRecord: (options: StartRecordOptions) => void;
  /** 停止录音 */
  stopRecord: () => void;
  /** 暂停录音 */
  pauseRecord: () => void;
  /** 继续录音 */
  continueRecord: () => void;
}

export default function useRecord({
  onRecordMessage,
  onStartRecord,
  onContinueRecord,
  onPauseRecord,
  onStopRecord,
}: {
  onRecordMessage: (params: IRecordMessage) => void;
  onStartRecord?: () => void;
  onContinueRecord?: () => void;
  onPauseRecord?: () => void;
  onStopRecord?: () => void;
}): RecordInfo {
  const [recordStatus, setRecordStatus] = useState<RECORD_STATUS>(RECORD_STATUS.DEFAULT);
  const { startTimer, stopTimer, resetTimer, formattedTime } = useTimer();
  const [conversationId, setConversationId] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [fillMode, setFillMode] = useState('');
  // recorder-core的实例
  const recorderRef = useRef(null);
  // ws连接，用于实时转译接口
  const wsRef = useRef(null);
  let sampleBuf = new Int16Array();

  const hightlightText = useCallback(
    async (messageId, text) => {
      if (!text || !keywords?.length) return;
      // TODO: 待实现文本替换

      onRecordMessage({
        id: messageId,
        text,
        method: 'update',
        format: true,
      });
    },
    [keywords, onRecordMessage],
  );

  const onSocketMessage = useCallback(
    (event, _fillMode) => {
      const msg = JSON.parse(event.data);
      const { mode, text } = msg;
      if (!text) return;
      const isNewMessage =
        !textBuffer.current || textBuffer.current.length > TEXT_BUFFER_MAX_LENGTH;

      // 消息切片时，更新上一个消息的文本
      if (textBuffer.current.length > TEXT_BUFFER_MAX_LENGTH && textBuffer.messageId) {
        hightlightText(textBuffer.messageId, textBuffer.current);
      }

      // 按模式更新和存储文本，同时离线的优先级更高，也就是收到离线消息时，将在线文本的cache替换成离线的
      if (mode === MODE_OFFLINE) {
        if (isNewMessage) {
          textBuffer.offline = text;
        } else {
          textBuffer.offline += text;
        }
        textBuffer.current = textBuffer.offline;
        textBuffer.online = textBuffer.offline;
      } else if (mode === MODE_ONLINE) {
        if (isNewMessage) {
          textBuffer.online = text;
          textBuffer.offline = '';
        } else {
          textBuffer.online += text;
        }
        textBuffer.current = textBuffer.online;
      }

      if (_fillMode === FILL_MODE.INPUT) {
        onRecordMessage({
          text: textBuffer.current,
          method: 'setInputText',
        });
        return;
      }

      if (isNewMessage) {
        const messageId = uuidv4();
        textBuffer.messageId = messageId;
        onRecordMessage({
          id: messageId,
          text: textBuffer.current,
          method: 'add',
        });
      } else {
        onRecordMessage({
          id: textBuffer.messageId,
          text: textBuffer.current,
          method: 'update',
        });
      }
    },
    [onRecordMessage, hightlightText],
  );

  const recProcess = useCallback((buffer, powerLevel, bufferDuration, bufferSampleRate) => {
    const data_48k = buffer[buffer.length - 1];
    const array_48k = new Array(data_48k);
    const data_16k = Recorder?.SampleData(array_48k, bufferSampleRate, 16000).data;

    sampleBuf = Int16Array.from([...sampleBuf, ...data_16k]);
    const chunk_size = 960; // for asr chunk_size [5, 10, 5]
    while (sampleBuf.length >= chunk_size) {
      const sendBuf = sampleBuf.slice(0, chunk_size);
      sampleBuf = sampleBuf.slice(chunk_size, sampleBuf.length);
      wsRef.current.send(sendBuf);
    }
  }, []);

  // 开始录音
  const startRecord = useCallback(
    (options: StartRecordOptions) => {
      const {
        conversationId: _conversationId = '',
        keywords: _keywords = [],
        fillMode: _fillMode = FILL_MODE.MESSAGE,
        minPowerLevel = 1,
        maxPowerLevel = 100,
      } = options;

      if (wsRef.current) {
        CnMessage.notice(
          $i18n.get({ id: 'InAudioProcessingPleaseWait', dm: '音频处理中，请稍后' }),
        );
        return;
      }

      // 初始化recorder实例
      if (!recorderRef.current) {
        recorderRef.current = Recorder({
          type: 'pcm',
          bitRate: 16,
          sampleRate: 16000,
          onProcess: (buffer, powerLevel, bufferDuration, bufferSampleRate) => {
            if (powerLevel >= minPowerLevel && powerLevel <= maxPowerLevel) {
              recProcess(buffer, powerLevel, bufferDuration, bufferSampleRate);
            }
          },
          audioTrackSet: {
            echoCancellation: true,
            noiseSuppression: true,
          },
        });
      }

      // 开始录音，调用录音api
      recorderRef.current.open(() => {
        recorderRef.current.start();
      });

      // 创建 WebSocket 连接
      wsRef.current = new WebSocket(WEBSOCKET_URL); // 替换为你的 WebSocket URL

      // 处理消息
      wsRef.current.onmessage = (event) => {
        onSocketMessage(event, _fillMode);
      };

      wsRef.current.onopen = () => {
        const hotwords = {};
        _keywords.forEach((k) => {
          hotwords[`${k}`] = 10;
        });
        const initWSParams = {
          ...getCommonWSParams(),
          is_speaking: true,
          conversationId: _conversationId,
          hotwords: JSON.stringify(hotwords),
        };
        wsRef.current.send(JSON.stringify(initWSParams));
      };
      setConversationId(_conversationId);
      setKeywords(_keywords);
      setFillMode(_fillMode);

      textBuffer.messageId = '';
      textBuffer.offline = '';
      textBuffer.online = '';
      textBuffer.current = '';

      resetTimer();
      startTimer();
      setRecordStatus(RECORD_STATUS.RECORDING);
      if (typeof onStartRecord === 'function') {
        onStartRecord();
      }
    },
    [recProcess, onSocketMessage, startTimer, resetTimer, onStartRecord],
  );

  // 继续录音
  const continueRecord = useCallback(() => {
    // 开始录音，调用录音api
    recorderRef.current.open(() => {
      recorderRef.current.start();
    });
    startTimer();
    setRecordStatus(RECORD_STATUS.RECORDING);
    if (typeof onContinueRecord === 'function') {
      onContinueRecord();
    }
  }, [startTimer, onContinueRecord]);

  // 暂停录音
  const pauseRecord = useCallback(() => {
    if (!recorderRef.current) {
      return;
    }
    recorderRef.current.close();
    stopTimer();
    setRecordStatus(RECORD_STATUS.PAUSED);
    if (typeof onPauseRecord === 'function') {
      onPauseRecord();
    }
  }, [stopTimer, onPauseRecord]);

  // 停止录音
  const stopRecord = useCallback(() => {
    if (!recorderRef.current) {
      return;
    }
    recorderRef.current.close();

    const endWSParams = {
      ...getCommonWSParams(),
      is_speaking: false,
      conversationId,
    };

    if (sampleBuf.length > 0) {
      wsRef.current.send(sampleBuf);
      sampleBuf = new Int16Array();
    }

    wsRef.current.send(JSON.stringify(endWSParams));

    // 停止录音时，更新当前消息的文本
    if (textBuffer.current && textBuffer.messageId) {
      hightlightText(textBuffer.messageId, textBuffer.current);
    }

    if (fillMode === FILL_MODE.INPUT) {
      wsRef.current.close();
      wsRef.current = null;
    } else {
      setTimeout(() => {
        wsRef.current.close();
        wsRef.current = null;
      }, 3000);
    }

    stopTimer();
    setRecordStatus(RECORD_STATUS.DEFAULT);
    if (typeof onStopRecord === 'function') {
      onStopRecord();
    }
  }, [conversationId, stopTimer, hightlightText, fillMode]);

  return {
    recordStatus,
    recordTime: formattedTime,
    startRecord,
    pauseRecord,
    continueRecord,
    stopRecord,
  };
}
